import { Controller } from "@hotwired/stimulus";

import { useEmailSubjectEditor } from "@bryq/employers-src/behaviors/use_email_subject_editor";
import { useEmailTextEditor } from "@bryq/employers-src/behaviors/use_email_text_editor";
import { deserialize } from "@bryq/employers-src/utils/text_editor/serializers";

export default class extends Controller {
  static targets = ["body", "subject", "resetButton", "submitButton"];

  static classes = ["hidden"];

  static values = {
    emailVariables: Array,
    defaultSubject: String,
    defaultBody: String,
  };

  /**
   * Checks if the `subject` and `body` fields are the default values.
   */
  get emailIsDefault() {
    const isDefault =
      this.subjectTarget.value === this.defaultSubjectValue &&
      this.bodyTarget.value.replace(/\n/g, "") ===
        this.defaultBodyValue.replace(/\n/g, "");
    return isDefault;
  }

  subjectTargetConnected(target) {
    this.subjectEditor = useEmailSubjectEditor(this, target, {
      variables: this.emailVariablesValue,
    });
  }

  bodyTargetConnected(target) {
    this.bodyEditor = useEmailTextEditor(this, target, {
      variables: this.emailVariablesValue,
    });
  }

  resetButtonTargetConnected(target) {
    target.classList.remove(this.hiddenClass);
    // eslint-disable-next-line no-param-reassign
    target.disabled = this.emailIsDefault;
  }

  /**
   * Enables or disables the reset button based on the subject and body values.
   */
  toggleResetButton() {
    this.resetButtonTarget.disabled = this.emailIsDefault;
  }

  /**
   * Resets the subject and body fields to their default values.
   */
  resetSubjectAndBody() {
    this.subjectEditor.commands.setContent(
      deserialize(this.defaultSubjectValue, this.emailVariablesValue),
      true,
    );
    this.bodyEditor.commands.setContent(
      deserialize(this.defaultBodyValue, this.emailVariablesValue),
      true,
    );
    this.toggleResetButton();
  }

  /**
   * Submits the form for validation when its dialog is discarded.
   *
   * This is required so that the email editor field updates with the correct
   * content, and any error messages that were there before discarding are
   * removed.
   */
  dialogDiscarded(e) {
    const { target } = e;
    if (this.element.contains(target)) {
      this.submitForm();
    }
  }

  submitForm() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.click();
    }
  }
}
